import Colors from '@/styles/Colors.module.scss'
import formStyles from '@/styles/Forms.module.scss'
import publicSciStyles from '@/styles/components/PublicSelfCheckIn.module.scss'
import utilityStyles from '@/styles/UtilityStyles.module.scss'
import usePublicSelfCheckIn from '@/hooks/api-hooks/usePublicSelfCheckIn'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import InputField from '@/components/common/fields/InputField'
import SelectField from '@/components/common/fields/SelectField'
import CheckboxField from '@/components/common/fields/CheckboxField'
import { Dialog } from '@mui/material'
import classNames from 'classnames'
import useWaitTimesByLocationId from '@/hooks/api-hooks/useWaitTimesByLocationId'
import * as Yup from 'yup'
import {
  createPublicSelfCheckin,
  postGuestExists,
} from '@/api/nextme/self-check-ins'
import Head from 'next/head'
import CreateEditPartyCustomFields from '@/components/pages/waitlist/CreateEditParty/CreateEditPartyCustomFields'
import { CustomField } from '@/api/interface/custom-field'
import { Queue } from '@/api/interface/queue'
import { dedupeArr, getDefaultCustomFieldValue } from '@/utils/util-functions'
import { dayjs } from '@/utils/dayjs'
import {
  customFieldsValidator,
  validateIntlPhone,
} from '@/utils/formik-validators'
import { mapqueueselectOptions } from '@/utils/queue-utils'
import SuccessDialogContent from '@/components/pages/manage/self-check-in/SuccessDialogContent'
import { useEffectOnce } from 'react-use'
import LocationProximityContent from '@/components/pages/manage/self-check-in/LocationProximityContent'
import LocationRequestContent from '@/components/pages/manage/self-check-in/LocationRequestContent'
import { resetGeolocation, setGeolocation } from '@/store/slices/partySlice'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from '@/store/index'
import { GeolocationOutOfRangeError } from '@/utils/error-reasons'
import usePublicBusiness from '@/hooks/api-hooks/usePublicBusiness'
import { planTypes } from '@/consts/Subscriptions'
import { closedMessageDefault } from '@/consts/sci-messages'
import { isUsingCustomQueueSelector } from '@/utils/custom-field-utils'
import CreateEditPartyCustomQueueSelector from '@/components/pages/waitlist/CreateEditParty/CreateEditPartyCustomQueueSelector'
import { getDisplayTimeDuration, filterUnits } from '@/utils/time-utils'
import InternationalPhoneInput from '@/components/common/forms/formik/InternationalPhoneInput2'
import { intlPhoneTest } from '@/utils/formik-validators'
import usePublicBusinessCountry from '@/hooks/usePublicBusinessCountry'
import {
  getCustomQueueSelectOptions,
  getStandardQueueSelectOptions,
  getShouldUseBothQueueSelectors,
} from '@/utils/queue-utils'
import PublicSciTerms from '@/components/pages/public/PublicSciTerms'
import RepeatGuestLookup from '@/components/pages/manage/self-check-in/RepeatGuestsPhoneField'
import { ReturningGuestData } from '@/api/interface/self-check-in'
import {
  getCustomQueueSelectReturningValue,
  getPrefillCustomFieldValue,
} from '@/utils/sci-utils'
import { isValidDateFieldValue } from '@/utils/validators'
import { HelperText } from '@/components/common/forms/Common'
import useMobileCookies from '@/hooks/useMobileCookies'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import SciErrorDialogContent from '@/components/pages/manage/self-check-in/SciErrorDialogContent'

export default function SelfCheckInPublicPage() {
  const dispatch = useDispatch()
  const router = useRouter()
  const { locationIdCookieVal } = useMobileCookies()

  const { locationId } = router.query

  const hasMobileCookie = useMemo(() => {
    return (
      Boolean(locationIdCookieVal) && Boolean(locationIdCookieVal?.length > 0)
    )
  }, [locationIdCookieVal])

  const { geolocation } = useTypedSelector((state) => state.party)

  const [TOCCheckboxError, setTOCCheckboxError] = useState(false)
  const [geofenceError, setGeofenceError] = useState(false)
  const [termsDialogOpen, setTermsDialogOpen] = useState(false)
  const [partyCreatedOpen, setPartyCreatedOpen] = useState(false)
  const [queueForWaitTime, setQueueForWaitTime] = useState(null)
  const [customQueueSelectValue, setCustomQueueSelectValue] = useState(null)
  const [isQueueIdDirty, setIsQueueIdDirty] = useState(false)
  const [wasGeoChecked, setWasGeoChecked] = useState(false)
  const [genericError, setGenericError] =
    useState<{ title?: string; message?: string }>(null)

  let dialogTimeout: NodeJS.Timeout | number

  const { publicSelfCheckIn, error: publicSciError } = usePublicSelfCheckIn(
    locationId as string
  )

  const orderedQueues = useMemo(() => {
    return publicSelfCheckIn?.queues?.sort((a, b) => {
      if (a.index < b.index) {
        return -1
      }
      if (a.index > b.index) {
        return 1
      }
      return 0
    })
  }, [publicSelfCheckIn])

  const { business } = usePublicBusiness(publicSelfCheckIn.businessId)
  const { countryISO = 'us' } = usePublicBusinessCountry(
    publicSelfCheckIn?.businessId,
    true
  )

  const businessCountry = useMemo(() => {
    return business?.address?.country?.toLowerCase()
  }, [business])

  const displayCoBrand = useMemo(() => {
    if (!business) return true

    return (
      business.subscriptionType !== planTypes.BUSINESS &&
      business.subscriptionType !== planTypes.CUSTOM &&
      business.subscriptionType !== planTypes.TRIAL
    )
  }, [business])

  const { waitTimes } = useWaitTimesByLocationId(locationId as string)

  const errorStatus = useMemo(
    () => publicSciError?.response?.status,
    [publicSciError]
  )

  const settings = useMemo(
    () => publicSelfCheckIn?.settings,
    [publicSelfCheckIn]
  )

  const hasCustomTerms = useMemo(() => {
    return (
      Boolean(settings?.terms?.length > 0) &&
      Boolean(settings?.includeTermsCheckbox)
    )
  }, [settings])

  const hasFollowUpMessage = useMemo(() => {
    const hasMktgEntitlement =
      business?.subscriptionType === planTypes.BUSINESS ||
      business?.subscriptionType === planTypes.CUSTOM
    return Boolean(business?.hasFollowupContent) && hasMktgEntitlement
  }, [publicSelfCheckIn, business])

  const isGeoEnabled = useMemo(() => {
    return publicSelfCheckIn?.geofence?.isEnabled
  }, [publicSelfCheckIn])

  /**
   * Custom fields
   * Recognize returning guests
   */
  const allCustomFields = useMemo(() => {
    const allCfs = orderedQueues?.reduce((acc, d) => {
      d.customFields?.forEach((field) => {
        if (!acc[field.id]) {
          acc[field.id] = {
            ...field,
            enabledAt: [d.id],
          }
        } else {
          acc[field.id].enabledAt.push(d.id)
        }
      })
      return acc
    }, {})

    const flatCfs =
      allCfs &&
      Object.keys(allCfs).reduce((acc, d) => {
        acc.push(allCfs[d])
        return acc
      }, [])

    const enabledCfs =
      flatCfs &&
      flatCfs?.filter((cf) => {
        return settings?.customFields?.[cf.id]
      })

    return dedupeArr(enabledCfs)
  }, [publicSelfCheckIn, orderedQueues])

  const customQueueSelectFieldIndex = useMemo(() => {
    return allCustomFields?.findIndex((cf) => cf.type === 'queseselect')
  }, [allCustomFields])

  const hasActiveCustomQueueSelector = useMemo(() => {
    return isUsingCustomQueueSelector(allCustomFields)
  }, [allCustomFields])

  const defaultCustomQueueOption = useMemo(() => {
    if (allCustomFields) {
      const customQueueSelect = allCustomFields?.find(
        (cf) => cf.type === 'queueselect'
      )
      return customQueueSelect?.options?.[0]
    }
    return undefined
  }, [allCustomFields])

  const [guestMatchFound, setGuestMatchFound] = useState(false)
  const isRecognizeRepeatGuestsEnabled = useMemo(() => {
    return settings?.require2FA === 'returning' // && settings?.recognizeRepeatGuests
  }, [settings])

  const [shouldDisplayMatchMessage, setShouldDisplayMatchMessage] =
    useState(false)
  const [isGuestLookingUp, setIsGuestLookingUp] = useState(false)
  const [isGuestLookupSuccess, setIsGuestLookupSuccess] = useState(false)
  const [lastValid2faPhone, setLastValid2faPhone] = useState('')
  const [is2FAModalOpen, setIs2faModalOpen] = useState(false)
  const [repeatGuestData, setRepeatGuestData] =
    useState<ReturningGuestData>(null)
  const [isFormPrefilling, setIsFormPrefilling] = useState(false)

  const checkIfGuestExists = useCallback(
    async (phone: string, setFieldValue) => {
      const isPhoneValid = await validateIntlPhone(phone)
      if (
        phone !== lastValid2faPhone &&
        isPhoneValid &&
        typeof locationId === 'string'
      ) {
        setIsGuestLookingUp(true)
        const status = await postGuestExists({ locationId, phone })

        if (status === 200) {
          // set guest exists
          setGuestMatchFound(true)
          // open modal
          setIs2faModalOpen(true)
          // display match message
          setShouldDisplayMatchMessage(true)
          setIsGuestLookupSuccess(true)
          setLastValid2faPhone(phone)
          setIsGuestLookingUp(false)
        } else {
          // reset queue and guest data
          setGuestMatchFound(false)
          setQueueForWaitTime('')
          setShouldDisplayMatchMessage(true)
          setIsGuestLookingUp(false)
          setIsGuestLookupSuccess(false)
          setRepeatGuestData(null)
          setIsFormPrefilling(false)
          setLastValid2faPhone('')
          setFieldValue('guest.firstName', '')
          setFieldValue('guest.lastName', '')
        }
      }
    },
    [
      locationId,
      lastValid2faPhone,
      setLastValid2faPhone,
      setGuestMatchFound,
      isRecognizeRepeatGuestsEnabled,
    ]
  )

  const resetRepeatGuestState = () => {
    setGuestMatchFound(false)
    setShouldDisplayMatchMessage(false)
    setIsGuestLookingUp(false)
    setIsGuestLookupSuccess(false)
    setLastValid2faPhone('')
    setRepeatGuestData(null)
    setIsFormPrefilling(false)
  }

  const prefillForm = useCallback(
    ({ returningGuestData, values, setFieldValue }) => {
      if (!isRecognizeRepeatGuestsEnabled || !returningGuestData) return
      const {
        customFieldData,
        totalVisits,
        lastVisit,
        deletedAt,
        createdAt,
        updatedAt,
        notes,
        ...rest
      } = returningGuestData

      const returningGuestQueueId = returningGuestData.lastVisit?.queueId
      // check if prefill queue id is available on this sci
      const prefillQueueId = orderedQueues?.find(
        (q) => q.id === returningGuestQueueId
      )
        ? returningGuestQueueId
        : orderedQueues?.[0]?.id

      const customQueueSelect = allCustomFields?.find(
        (cf) => cf.type === 'queueselect'
      )

      const customQueueCfValue = getCustomQueueSelectReturningValue(
        customQueueSelect,
        returningGuestData
      )

      const splitName = returningGuestData.name?.split(' ')

      const prefillGuestData = {
        ...values.guest,
        firstName: splitName?.[0] || '',
        lastName:
          splitName?.length > 1 ? splitName?.[splitName?.length - 1] : '',
      }

      Object.keys(values.guest).forEach((key) => {
        setFieldValue(`guest.${key}`, prefillGuestData[key])
      })
      if (prefillQueueId) {
        setQueueForWaitTime(prefillQueueId)
      }

      if (customQueueCfValue) {
        setFieldValue('customQueueSelect', customQueueCfValue)
      }

      setFieldValue('queueId', prefillQueueId)
      setIsQueueIdDirty(true)
      setIsFormPrefilling(true)
      setRepeatGuestData(returningGuestData)
      setIs2faModalOpen(false)
    },
    [
      isRecognizeRepeatGuestsEnabled,
      locationId,
      allCustomFields,
      orderedQueues,
      setQueueForWaitTime,
      setIsQueueIdDirty,
    ]
  )

  const parseClosedMessageSubtitle = useCallback(
    (text: string) => {
      const subtitle = text ? text : closedMessageDefault.SUBTITLE
      return subtitle.replace(
        '{{nextOpen}}',
        dayjs(publicSelfCheckIn?.schedule?.nextOpen?.date).calendar()
      )
    },
    [publicSelfCheckIn]
  )

  const attemptGetPosition = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const {
          coords: { latitude: lat, longitude: long, accuracy: acc },
        } = position
        dispatch(setGeolocation({ lat, long, acc }))
      },
      (err) => {
        // { code: 1, message: 'User denied Geolocation' }
        const { code, message } = err
        dispatch(setGeolocation({ error: { code, message } }))
      }
    )
  }

  useEffectOnce(() => {
    if (errorStatus === 422) return
  })

  useEffect(() => {
    if (isGeoEnabled && !wasGeoChecked) {
      attemptGetPosition()
      setWasGeoChecked(true)
    }
  }, [isGeoEnabled, wasGeoChecked])

  /**
   * Support both queue selectors side by side if warranted
   */

  const customQueueSelector = useMemo(() => {
    return allCustomFields?.find((cf) => cf.type === 'queueselect')
  }, [allCustomFields])

  const customQueueSelectOptions = useMemo(() => {
    if (customQueueSelector && waitTimes) {
      return getCustomQueueSelectOptions([customQueueSelector], waitTimes)
    }
  }, [allCustomFields, customQueueSelector, waitTimes])

  const standardQueueSelectOptions = useMemo(() => {
    if (orderedQueues && customQueueSelectOptions && waitTimes) {
      return getStandardQueueSelectOptions(
        orderedQueues,
        waitTimes,
        customQueueSelectOptions
      )
    }

    return mapqueueselectOptions(orderedQueues, waitTimes)
  }, [orderedQueues, customQueueSelectOptions, waitTimes])

  const [publicFieldsDefaultQueue, setPublicFieldsDefaultQueue] = useState(null)

  useEffect(() => {
    setPublicFieldsDefaultQueue(settings?.publicFields?.defaultQueue)
  }, [settings?.publicFields?.defaultQueue, publicSelfCheckIn])

  const defaultStandardQueue = useMemo(() => {
    if (
      !publicFieldsDefaultQueue ||
      !orderedQueues ||
      !standardQueueSelectOptions ||
      !waitTimes
    ) {
      return undefined
    }

    // case: settings.publicFields.defaultQueue is nextAvailable
    if (
      publicFieldsDefaultQueue &&
      publicFieldsDefaultQueue === 'nextAvailable'
    ) {
      const nextAvailableQueue = [...standardQueueSelectOptions]?.find(
        (item) => {
          const maxCapacity = orderedQueues?.find(
            (x) => x.id === item.id
          )?.maxCapacity
          const waitingParties = waitTimes?.find(
            (x) => x.queueId === item.id
          )?.waitingPartiesInQueue
          return maxCapacity < 0 || maxCapacity > waitingParties
        }
      )
      return nextAvailableQueue?.id ?? orderedQueues?.[0]?.queueId
    }

    // case: settings.publicFields.defaultQueue is set, but it is not a valid queue for this SCI
    if (
      publicFieldsDefaultQueue &&
      publicFieldsDefaultQueue !== 'nextAvailable' &&
      ![...orderedQueues]?.find((x) => x.id === publicFieldsDefaultQueue)
    ) {
      return orderedQueues?.[0]?.queueId
    }

    if (
      publicFieldsDefaultQueue &&
      publicFieldsDefaultQueue !== 'nextAvailable'
    ) {
      return publicFieldsDefaultQueue
    }

    // case: no settings.publicFields.defaultQueue set
    if (!publicFieldsDefaultQueue) {
      return orderedQueues?.[0]?.queueId
    }

    // case: settings.publicFields.defaultQueue is set and it is valid
    return publicFieldsDefaultQueue
  }, [
    publicFieldsDefaultQueue,
    waitTimes,
    standardQueueSelectOptions,
    publicSelfCheckIn,
    orderedQueues?.[0],
    orderedQueues,
  ])

  useEffect(() => {
    if (
      !isRecognizeRepeatGuestsEnabled &&
      !settings?.publicFields?.queueSelect &&
      orderedQueues &&
      orderedQueues?.length > 0 &&
      defaultStandardQueue
    ) {
      setQueueForWaitTime(defaultStandardQueue)
    }
  }, [
    settings,
    isRecognizeRepeatGuestsEnabled,
    orderedQueues,
    defaultStandardQueue,
    setQueueForWaitTime,
  ])

  const isUsingBothQueueSelectors = useMemo(() => {
    return getShouldUseBothQueueSelectors(
      orderedQueues,
      customQueueSelectOptions
    )
  }, [orderedQueues, customQueueSelectOptions])

  const selectedQueueWaitTime = useMemo(() => {
    const waitTime = waitTimes?.find((x) => x.queueId === queueForWaitTime)
    return waitTime
  }, [waitTimes, queueForWaitTime, settings])

  const buildCfValues = (cfs: Array<CustomField>) => {
    return cfs
      ?.sort((a, b) => {
        if (a.index > b.index) {
          return 1
        }
        if (a.index < b.index) {
          return -1
        }
        return 0
      })
      ?.map((x) => {
        const prefillValue = repeatGuestData?.latestCustomFieldAnswers?.find(
          (item) => item.customFieldId === x.id
        )?.customFieldValue
        const isFieldPrefillValueEmpty =
          isRecognizeRepeatGuestsEnabled && repeatGuestData && !prefillValue
        const cfValue =
          isRecognizeRepeatGuestsEnabled && repeatGuestData && guestMatchFound
            ? getPrefillCustomFieldValue({
                cf: x,
                cfAnswers: repeatGuestData?.latestCustomFieldAnswers,
              })
            : getDefaultCustomFieldValue(
                x.type,
                x.options,
                customQueueSelectValue,
                queueForWaitTime
              )

        const isDateFieldValid =
          x.type === 'date' ? isValidDateFieldValue(cfValue as string) : true
        const disabled =
          isRecognizeRepeatGuestsEnabled &&
          guestMatchFound &&
          x.editableByGuest !== undefined &&
          x.editableByGuest !== null &&
          !Boolean(x.editableByGuest) &&
          !isFieldPrefillValueEmpty &&
          isDateFieldValid

        const isVisibleByGuestType = isRecognizeRepeatGuestsEnabled
          ? guestMatchFound
            ? x.guestTypes !== 'new'
            : x.guestTypes !== 'returning'
          : true
        const firstQueueId = orderedQueues?.[0]?.id
        const isVisibleByQueue = x.enabledAt?.includes(
          queueForWaitTime || firstQueueId
        )

        const isRequired =
          x.isRequired && isVisibleByGuestType && isVisibleByQueue

        return {
          ...x,
          disabled,
          isRequired,
          hideField: !isVisibleByGuestType || !isVisibleByQueue,
          value: cfValue,
        }
      })
  }

  useEffect(() => {
    if (partyCreatedOpen) {
      setTimeout(
        () => document.getElementById('party-created-dialog-backdrop')?.click(),
        5000
      )
    }
  }, [partyCreatedOpen])

  const defaultQueueForWaitTime = useMemo(() => {
    if (!hasActiveCustomQueueSelector && !settings?.publicFields?.queueSelect) {
      return defaultStandardQueue ?? ''
    }
    if (!hasActiveCustomQueueSelector && settings?.publicFields?.queueSelect) {
      return ''
    }
    return defaultCustomQueueOption?.valueMeta
  }, [
    standardQueueSelectOptions,
    defaultCustomQueueOption,
    publicSelfCheckIn,
    hasActiveCustomQueueSelector,
    isUsingBothQueueSelectors,
    defaultStandardQueue,
  ])

  const initQueueWaitTime = useMemo(() => {
    const currentQueueForWaitTime = waitTimes?.find(
      (item) => item.queueId === queueForWaitTime
    )?.waitTimeM
    return currentQueueForWaitTime
  }, [waitTimes, queueForWaitTime])

  const initialValues = useMemo(() => {
    const cfValues = buildCfValues(allCustomFields)
    const customQueueSelectValue = cfValues?.find(
      (item) => item.type === 'queueselect'
    )?.value

    return {
      guest: {
        firstName: '',
        lastName: '',
        phone: '',
      },
      customFields: cfValues,
      customQueueSelect: customQueueSelectValue,
      queueId: defaultQueueForWaitTime,
      ...(settings?.includeTermsCheckbox && { agreeToc: false }),
      agreeToc: false,
    }
  }, [allCustomFields, settings, defaultQueueForWaitTime])

  useEffect(() => {
    if (!queueForWaitTime) {
      setQueueForWaitTime(defaultQueueForWaitTime)
    }
  }, [defaultQueueForWaitTime])

  const validationSchema = Yup.object({
    guest: Yup.object({
      firstName: settings?.publicFields?.firstName
        ? Yup.string().required('Required')
        : undefined,
      lastName: settings?.publicFields?.lastName
        ? Yup.string().required('Required')
        : undefined,
      phone: settings?.publicFields?.phone
        ? Yup.string()
            .test(
              'passes-google-libphonenumber-validation',
              'Invalid phone number',
              async (value) => {
                const passedIntlPhoneTest = await intlPhoneTest(value)
                return passedIntlPhoneTest
              }
            )
            .test('custom-required-test', 'Required', (value) => {
              return value?.length >= 5
            })
        : undefined,
    }),
    customFields: customFieldsValidator(customQueueSelectValue),
    queueId:
      settings?.publicFields?.queueSelect && !isUsingBothQueueSelectors
        ? Yup.string()
            .required('Required')
            .test('lengthTest', 'Required', (value) => {
              return typeof value !== 'undefined' && value?.length > 1
            })
        : undefined,
  })

  const handleQueueChange = useCallback(
    (queueId, setFieldValue, values) => {
      // If queueId is set back to blank and using custom queue selector
      // set queueId to the value of custom queue select or first option
      if (hasActiveCustomQueueSelector && !queueId) {
        const qsCfValue = values?.customQueueSelect
        const qsCfValueMeta = customQueueSelectOptions?.find(
          (option) => option.value === qsCfValue
        )?.data?.valueMeta
        const firstCustomOptionValueMeta =
          customQueueSelectOptions?.[0]?.data?.valueMeta
        setQueueForWaitTime(qsCfValueMeta || firstCustomOptionValueMeta)
      } else {
        setQueueForWaitTime(queueId)
      }
      setFieldValue('queueId', queueId)
      queueId ? setIsQueueIdDirty(true) : setIsQueueIdDirty(false)
    },
    [hasActiveCustomQueueSelector, customQueueSelectOptions]
  )

  const handleCustomQueueFieldChange = useCallback(
    (value, setFieldValue, fieldName, valueMeta, values) => {
      const isQueueIdValueStandard = Boolean(
        standardQueueSelectOptions?.find(
          (option) => option.value === values?.queueId
        )
      )

      setFieldValue(fieldName, value)

      // only allow custom field to set queue if not displaying both selectors OR
      // displaying both selectors and the standard selector is not set
      if (
        !isUsingBothQueueSelectors ||
        (isUsingBothQueueSelectors && !isQueueIdValueStandard) ||
        (isUsingBothQueueSelectors && !values?.queueId)
      ) {
        setQueueForWaitTime(valueMeta)
      }

      setCustomQueueSelectValue(value)
    },
    [isUsingBothQueueSelectors, standardQueueSelectOptions, queueForWaitTime]
  )

  const onFormReset = (_values, helpers: FormikHelpers<any>) => {
    handleQueueChange('', helpers.setFieldValue, initialValues)
    setQueueForWaitTime(defaultQueueForWaitTime)
    setIsQueueIdDirty(false)
    setIsFormPrefilling(false)
    setRepeatGuestData(null)
    resetRepeatGuestState()
    if (hasActiveCustomQueueSelector) {
      handleCustomQueueFieldChange(
        '',
        helpers.setFieldValue,
        'customQueueSelect',
        waitTimes?.[0]?.queueId,
        initialValues
      )
    }

    helpers.setFieldValue('guest', initialValues.guest)

    if (settings.includeTermsCheckbox) {
      helpers.setFieldValue('agreeToc', false)
    }

    helpers.setFieldValue('customFields', initialValues.customFields)
  }

  const getQuotedWaitTime = useCallback(
    (qId) => {
      return waitTimes?.reduce((acc, d) => {
        if (qId && d.queueId === qId) {
          acc = d.waitTimeS
        }

        return acc
      }, 0)
    },
    [waitTimes]
  )

  const setCustomQueueSelectCf = (values, customQueueSelectValue) => {
    const { customFields } = values
    const updatedFields = customFields.map((cf) => {
      if (cf.type === 'queueselect') {
        return {
          ...cf,
          value: customQueueSelectValue || defaultCustomQueueOption?.value,
        }
      }
      return cf
    })

    return updatedFields
  }

  const cleanCustomFieldsSubmission = useCallback(
    (queueId, customFields) => {
      return customFields.filter((item) => {
        const isVisibleByGuestType = isRecognizeRepeatGuestsEnabled
          ? guestMatchFound
            ? item.guestTypes !== 'new'
            : item.guestTypes !== 'returning'
          : true
        const isVisibleByQueue = item.enabledAt?.includes(queueId)
        return isVisibleByGuestType && isVisibleByQueue
      })
    },
    [isRecognizeRepeatGuestsEnabled, guestMatchFound]
  )

  const onSubmit = async (values: any) => {
    setTOCCheckboxError(false)
    // console.log('values before submit: ', values)
    // console.log(': ', values)
    const queueIdForSubmission =
      queueForWaitTime ||
      defaultCustomQueueOption?.data?.valueMeta ||
      waitTimes?.[0]?.queueId
    const modifiedCfs = setCustomQueueSelectCf(values, customQueueSelectValue)
    const cleanedCfs = cleanCustomFieldsSubmission(
      queueIdForSubmission,
      modifiedCfs
    )
    // console.log('cleaned cfs: ', cleanedCfs)
    if (values.agreeToc !== true) {
      setTOCCheckboxError(true)
      return
    }

    const geoValue =
      geolocation.lat && geolocation.long
        ? {
            ...geolocation,
            addressId: publicSelfCheckIn.locationAddressId,
          }
        : {}

    const submission = {
      ...values,
      locationId,
      quotedWaitTime: getQuotedWaitTime(queueIdForSubmission),
      guestId: repeatGuestData?.id || null,
      guest: {
        ...values.guest,
        id: repeatGuestData?.id || null,
        name: [values.guest.firstName, values.guest.lastName]
          .filter((x) => !!x)
          .join(' '),
        phone: values.guest.phone,
      },
      customFields: cleanedCfs,
      queueId:
        queueForWaitTime ||
        defaultCustomQueueOption?.data?.valueMeta ||
        defaultStandardQueue ||
        orderedQueues?.[0]?.id,
      geolocation: geoValue,
    }

    await createPublicSelfCheckin(submission, locationId as string)
      .then(() => {
        setPartyCreatedOpen(true)
        resetRepeatGuestState()
      })
      .catch((err) => {
        console.log(err)
        if (
          err?.response?.data?.errors?.reason?.includes(
            GeolocationOutOfRangeError
          )
        ) {
          setGeofenceError(true)
        } else if (err?.response?.data?.errors) {
          setGenericError({
            title: err?.response?.data?.message || 'Something went wrong',
            message:
              err?.response?.data?.errors?.reason ||
              'Something went wrong while checking you in. Please try again.',
          })
        }
      })
  }

  const waitTimeDisplayText = useMemo(() => {
    if (!queueForWaitTime || selectedQueueWaitTime?.waitTimeM < 1) {
      if (!initQueueWaitTime || initQueueWaitTime < 1) {
        return null
      }
      return ' Estimated Wait'
    } else {
      return ' Estimated Wait'
    }
  }, [queueForWaitTime, selectedQueueWaitTime, initQueueWaitTime])

  const isPageUnusable = useMemo(
    () => !publicSelfCheckIn.id || waitTimes?.length < 1,
    [publicSelfCheckIn, waitTimes]
  )

  const isScheduledOpen = useMemo(
    () =>
      settings?.useWaitlistHours == false ||
      publicSelfCheckIn?.schedule?.isOpenNow,
    [publicSelfCheckIn, settings]
  )

  const errorReason = useMemo(() => {
    return publicSciError?.response?.data?.errors?.reason
  }, [publicSciError])

  if (errorStatus === 422 && errorReason?.startsWith('Reach out to')) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          margin: '2rem',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <p>
          <span style={{ fontWeight: 'bold', fontSize: 20 }}>
            Self Check-in is currently disabled.
          </span>
          <br></br>
          <span>{errorReason}</span>
        </p>
      </div>
    )
  }
  if (isPageUnusable) return null

  if (!isScheduledOpen)
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          margin: '2rem',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <p>
          <span style={{ fontWeight: 'bold', fontSize: 20 }}>
            {settings.closedMessageTitle
              ? settings.closedMessageTitle
              : closedMessageDefault.TITLE}
          </span>
          <br></br>
          <span>
            {parseClosedMessageSubtitle(settings.closedMessageSubtitle)}
          </span>
        </p>
      </div>
    )

  return errorStatus === 422 ? (
    <div
      style={{
        maxWidth: '75%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <p>Self Check-in is currently disabled.</p>
    </div>
  ) : (
    <>
      <Head>
        <title>
          {settings?.overrides?.locationName ||
            publicSelfCheckIn?.sciable?.name}{' '}
          | Self Check-in
        </title>
      </Head>

      <div className={publicSciStyles.publicSciPageWrapper}>
        {hasMobileCookie && (
          <div
            className={publicSciStyles['mobile-btn--back']}
            role="button"
            aria-label="Go Back"
            onClick={() => {
              window.location.href = `${window.location.origin}/settings/self-check-in`
            }}
          >
            <ArrowLeftIcon />
          </div>
        )}
        <section id="left-half" className={publicSciStyles.leftHalf}>
          <img
            alt=""
            src={
              publicSelfCheckIn?.media?.logo?.originalUrl ||
              business?.media?.logo?.originalUrl ||
              '/logos/nextme/nextme-square-light.png'
            }
            className={publicSciStyles.logo}
            style={{
              borderRadius: 16,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
              height: 'auto',
            }}
          />
          <br />
          <h2 className={publicSciStyles.sciName}>
            {settings?.overrides?.locationName ||
              publicSelfCheckIn?.sciable?.name}
          </h2>
          {settings?.displayWaitTime && waitTimes?.length > 0 && (
            <p
              className={publicSciStyles.waitTime}
              style={{
                minHeight: '29px',
              }}
            >
              <span style={{ fontWeight: 'bold' }}>
                {queueForWaitTime && selectedQueueWaitTime?.waitTimeM > 0
                  ? `${filterUnits(
                      getDisplayTimeDuration(
                        selectedQueueWaitTime.waitTimeM,
                        'minutes',
                        2
                      ),
                      ['s']
                    )}`
                  : ''}
                {!queueForWaitTime && initQueueWaitTime && initQueueWaitTime > 0
                  ? `${filterUnits(
                      getDisplayTimeDuration(initQueueWaitTime, 'minutes', 2),
                      ['s']
                    )}`
                  : ''}
              </span>

              <span>{waitTimeDisplayText}</span>
            </p>
          )}
        </section>
        <section id="right-half" className={publicSciStyles.rightHalf}>
          <div
            className={classNames(
              utilityStyles.separator,
              publicSciStyles.halfLine
            )}
          ></div>

          <h2 style={{ maxWidth: '75%' }} className={publicSciStyles.formTitle}>
            {settings?.title || 'Join the Waitlist'}
          </h2>
          <p
            style={{
              fontWeight: 500,
              textAlign: 'center',
              maxWidth: '85%',
              fontSize: '16px',
              margin: '8px 0px',
            }}
          >
            <span>
              {settings?.subtitle ||
                "Complete and submit the form below to be added to the waitlist.\nWe will text you when it's your turn in line."}
            </span>
          </p>
          <br />
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            onReset={onFormReset}
          >
            {({
              isSubmitting,
              setFieldValue,
              setValues,
              setErrors,
              setTouched,
              values,
              resetForm,
              submitCount,
              errors,
              validateField,
              touched,
              setFieldTouched,
            }) => {
              // Prefill custom fields when queue changes if prefilling returning guest data
              useEffect(() => {
                if (repeatGuestData) {
                  // if is using both queue selectors, the queueId must match an option in one of the two queue selectors
                  const customFieldsAfterQueueChange =
                    buildCfValues(allCustomFields)
                  const queueselectCf = customFieldsAfterQueueChange?.find(
                    (item) => item.type === 'queueselect'
                  )
                  const queueselectCfValue = queueselectCf?.value

                  if (isUsingCustomQueueSelector && !queueselectCfValue) {
                    setFieldValue(
                      'customQueueSelect',
                      defaultCustomQueueOption?.value
                    )
                    setCustomQueueSelectValue(defaultCustomQueueOption?.value)
                  } else {
                    setFieldValue('customQueueSelect', queueselectCfValue)
                    setCustomQueueSelectValue(queueselectCfValue)
                  }

                  const customFieldsQueueSelectUpdated =
                    customFieldsAfterQueueChange?.map((cf) => {
                      if (cf.type === 'queueselect') {
                        return {
                          ...cf,
                          value:
                            isFormPrefilling && queueselectCfValue
                              ? queueselectCfValue
                              : defaultCustomQueueOption?.value,
                        }
                      }
                      return cf
                    })

                  setFieldValue('customFields', customFieldsQueueSelectUpdated)
                  setIsFormPrefilling(false)
                } else {
                  // case when phone changes to an unvalidated number
                  const initCfs = buildCfValues(allCustomFields)

                  setValues((values) => ({
                    ...values,
                    // queueId: queueForWaitTime,
                    guest: {
                      ...values.guest,
                      phone: values.guest?.phone || '',
                    },
                    customFields: initCfs,
                  }))
                  setIsFormPrefilling(false)
                }
              }, [
                allCustomFields,
                queueForWaitTime,
                repeatGuestData,
                isFormPrefilling,
              ])

              const handleCloseDialog = (reason?: string) => {
                if (reason !== 'genericError') {
                  setPartyCreatedOpen(false)
                  setTOCCheckboxError(false)
                  setGeofenceError(false)
                  resetForm()
                  scrollTo(0, 0)
                }
              }

              useEffect(() => {
                if (partyCreatedOpen) {
                  dialogTimeout = setTimeout(() => {
                    handleCloseDialog('closeTimeout')
                  }, 15000)
                }
                return () => clearTimeout(dialogTimeout)
              }, [partyCreatedOpen])

              return (
                <Form noValidate style={{ width: '75%' }}>
                  {settings?.publicFields?.phone && businessCountry && (
                    <div className="relative">
                      <InternationalPhoneInput
                        required
                        name="guest.phone"
                        label="Mobile Phone"
                        defaultCountry={businessCountry}
                        setFieldValue={setFieldValue}
                        submitCount={submitCount}
                        disableDialCodeAndPrefix={true}
                        showDisabledDialCodeAndPrefix={true}
                        handleBlur={async () => {
                          if (isRecognizeRepeatGuestsEnabled) {
                            checkIfGuestExists(
                              values?.guest?.phone,
                              setFieldValue
                            )
                          }
                        }}
                        handleChange={(phone: string) => {
                          const val = phone
                          setFieldValue('guest.phone', val)
                        }}
                        validateField={() => {
                          validateField('guest.phone')
                          setFieldTouched('guest.phone', true)
                        }}
                      />
                      {isRecognizeRepeatGuestsEnabled && (
                        <RepeatGuestLookup
                          isModalOpen={is2FAModalOpen}
                          setIsModalOpen={setIs2faModalOpen}
                          businessId={publicSelfCheckIn?.businessId}
                          locationId={locationId as string}
                          canCloseModal={true}
                          phone={values?.guest?.phone}
                          isPhoneValid={() => {
                            return (
                              // @ts-expect-error temp, check guest phone exists first?
                              Boolean(touched?.guest?.phone) &&
                              errors['guest.phone'] === undefined &&
                              validateIntlPhone(values?.guest?.phone)
                            )
                          }}
                          shouldLookupGuest={isRecognizeRepeatGuestsEnabled}
                          isGuestLookingUp={isGuestLookingUp}
                          isGuestLookupSuccess={isGuestLookupSuccess}
                          require2fa={settings?.require2FA}
                          setGuestData={setRepeatGuestData}
                          shouldDisplayMatchMessage={shouldDisplayMatchMessage}
                          handleClose={() => {
                            setIs2faModalOpen(false)
                            resetRepeatGuestState()
                            resetForm()
                          }}
                          prefillForm={async (returningGuestData) => {
                            await prefillForm({
                              returningGuestData,
                              values,
                              setFieldValue,
                            })
                          }}
                        />
                      )}
                    </div>
                  )}
                  {settings?.publicFields?.firstName && (
                    <InputField
                      autoComplete="given-name"
                      name="guest.firstName"
                      label="First Name"
                      fullWidth
                      required
                    />
                  )}
                  {settings?.publicFields?.lastName && (
                    <InputField
                      autoComplete="family-name"
                      name="guest.lastName"
                      label="Last Name"
                      fullWidth
                      required
                    />
                  )}

                  <div
                    id="queueselect-container"
                    style={{
                      display:
                        Boolean(customQueueSelectValue) &&
                        !isUsingBothQueueSelectors
                          ? 'none'
                          : 'block',
                    }}
                  >
                    {((settings?.publicFields?.queueSelect &&
                      !hasActiveCustomQueueSelector) ||
                      (hasActiveCustomQueueSelector &&
                        isUsingBothQueueSelectors)) && (
                      <SelectField
                        showEmptyOption
                        name="queueId"
                        label={settings?.overrides?.queueName || 'Queue'}
                        options={standardQueueSelectOptions}
                        required={!isUsingBothQueueSelectors}
                        onChange={(e) => {
                          handleQueueChange(
                            e.target.value,
                            setFieldValue,
                            values
                          )
                        }}
                        fullWidth
                      />
                    )}
                  </div>
                  {hasActiveCustomQueueSelector &&
                    waitTimes &&
                    customQueueSelector && (
                      <CreateEditPartyCustomQueueSelector
                        cf={customQueueSelector}
                        setFieldValue={setFieldValue}
                        publicQueueSelectHandler={handleCustomQueueFieldChange}
                        waitTimes={waitTimes}
                        values={values}
                      />
                    )}
                  {allCustomFields && (
                    <CreateEditPartyCustomFields
                      customFields={values.customFields}
                      publicQueueSelectHandler={handleCustomQueueFieldChange}
                      setFieldValue={setFieldValue}
                      currentQueueSelectOption={queueForWaitTime}
                    />
                  )}

                  <CheckboxField
                    name="agreeToc"
                    onChange={(e) => {
                      setTOCCheckboxError(false)
                      setFieldValue('agreeToc', e.target.checked)
                    }}
                    checked={Boolean(values?.agreeToc)}
                    options={[
                      {
                        component: (
                          <PublicSciTerms
                            hasFollowUpMessage={hasFollowUpMessage}
                            hasCustomTerms={hasCustomTerms}
                            openModal={() => setTermsDialogOpen(true)}
                          />
                        ),
                        value: true,
                      },
                    ]}
                  />
                  {TOCCheckboxError && (
                    <HelperText hasError={true}>
                      {TOCCheckboxError &&
                        'Please accept the terms and conditions.'}
                    </HelperText>
                  )}

                  <button
                    className={classNames('btn')}
                    style={{
                      width: '100%',
                      height: 60,
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      marginTop: '0.875rem',
                      marginBottom: '1rem',
                      borderRadius: '99px',
                      backgroundColor:
                        settings?.btnBackgroundColor || Colors['theme-green'],
                      color: settings?.btnTextColor || Colors['theme-white'],
                    }}
                    type="submit"
                    onClick={(e) => {
                      isSubmitting && e.preventDefault()
                    }}
                    disabled={isSubmitting}
                  >
                    Submit{isSubmitting ? 'ting...' : ''}
                  </button>

                  <Dialog
                    BackdropProps={{ id: 'party-created-dialog-backdrop' }}
                    open={
                      partyCreatedOpen ||
                      !!(
                        publicSelfCheckIn?.geofence?.isEnabled &&
                        (geolocation?.error || geofenceError)
                      ) ||
                      Boolean(genericError?.hasOwnProperty('title'))
                    }
                    onClose={(e, reason) => {
                      handleCloseDialog(reason)
                      window.location.reload()
                    }}
                    sx={{
                      '& .MuiDialog-paper': {
                        width: '70vw',
                        padding: '1.5rem',
                        maxWidth: '572px',
                        alignItems: 'center',
                        borderRadius: '16px',
                      },
                    }}
                  >
                    {/* TODO: rewrite as tailwind and update text/button sizing for phone users */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        maxWidth: 400,
                      }}
                    >
                      {partyCreatedOpen && (
                        <SuccessDialogContent
                          settings={settings}
                          handleCloseDialog={() => {
                            handleCloseDialog('closeButton')
                            window.location.reload()
                          }}
                        />
                      )}
                      {geofenceError && (
                        <LocationProximityContent
                          geofence={publicSelfCheckIn?.geofence}
                          settings={settings}
                          handleCloseDialog={(e) => {
                            setGeofenceError(false)
                            handleCloseDialog('closeButton')
                          }}
                          sciableName={publicSelfCheckIn?.sciable?.name}
                        />
                      )}
                      {!!publicSelfCheckIn?.geofence?.isEnabled &&
                        geolocation?.error && (
                          <LocationRequestContent
                            settings={settings}
                            handleCloseDialog={(e) => {
                              dispatch(resetGeolocation())
                              handleCloseDialog('closeButton')
                              attemptGetPosition()
                            }}
                            sciableName={publicSelfCheckIn?.sciable?.name}
                          />
                        )}
                      {genericError?.hasOwnProperty('title') && (
                        // error content
                        <SciErrorDialogContent
                          settings={settings}
                          errorTitle={genericError?.title}
                          errorMsg={genericError?.message}
                          handleCloseDialog={() => {
                            setGenericError(null)
                            handleCloseDialog('genericError')
                          }}
                        />
                      )}
                    </div>
                  </Dialog>
                </Form>
              )
            }}
          </Formik>
          {displayCoBrand && (
            <p style={{ fontWeight: 500 }}>
              powered by{' '}
              <a
                href="https://nextmeapp.com"
                target="_blank"
                style={{ fontWeight: 700 }}
              >
                NextMe
              </a>
            </p>
          )}
        </section>
        <Dialog
          open={termsDialogOpen}
          onClose={() => {
            setTermsDialogOpen(false)
          }}
          sx={{
            '& .MuiDialog-paper': {
              width: '70vw',
              padding: '2rem',
              borderRadius: '16px',
            },
          }}
        >
          <h2
            style={{ textAlign: 'left', marginBottom: '.5rem' }}
            className={publicSciStyles.formTitle}
          >
            Terms and Conditions
          </h2>
          <p>{settings?.terms}</p>
        </Dialog>
      </div>
    </>
  )
}
